import { useState } from 'react';
import { usePermissions } from '@/features/auth';
import type { ThirdPartyInvoiceableDealer } from '../types/thirdPartyAgreement.types';
import {
    selectedInvoicingDealer,
    thirdPartyDealerFieldState,
    type ThirdPartyDealerFieldState,
} from '../utils/thirdPartyInvoicingDealer';
import { useThirdPartyInvoiceableDealersQuery } from './useThirdPartyInvoiceableDealersQuery';

export interface ThirdPartyInvoicingDealer {
    field: ThirdPartyDealerFieldState;
    dealers: ThirdPartyInvoiceableDealer[];
    selected: ThirdPartyInvoiceableDealer | null;
    selectedId: string | null;
    select: (dealer: ThirdPartyInvoiceableDealer | null) => void;
    retry: () => void;
    retrying: boolean;
}

const NO_DEALERS: ThirdPartyInvoiceableDealer[] = [];

interface ChoiceState {
    agreement: string;
    dealers: ThirdPartyInvoiceableDealer[];
    choice: string | null;
}

export function useThirdPartyInvoicingDealer(vrm: string, reference: string): ThirdPartyInvoicingDealer {
    const { hasPermission } = usePermissions();
    const permitted = hasPermission({ resource: 'invoice', action: 'add' });
    const query = useThirdPartyInvoiceableDealersQuery(vrm, reference, permitted);
    const dealers = query.data ?? NO_DEALERS;
    const agreement = `${vrm}:${reference}`;
    const [state, setState] = useState<ChoiceState>({ agreement, dealers, choice: null });

    if (state.agreement !== agreement) {
        setState({ agreement, dealers, choice: null });
    } else if (state.dealers !== dealers) {
        const kept = dealers.some((dealer) => dealer.encryptedId === state.choice);
        setState({ agreement, dealers, choice: kept ? state.choice : null });
    }

    const selected = selectedInvoicingDealer(dealers, state.choice);

    return {
        field: thirdPartyDealerFieldState(permitted, query),
        dealers,
        selected,
        selectedId: selected?.encryptedId ?? null,
        select: (dealer) => setState((current) => ({ ...current, choice: dealer?.encryptedId ?? null })),
        retry: () => {
            void query.refetch();
        },
        retrying: query.isFetching,
    };
}
